import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { fetchAvailability, createBooking } from "../../functions/booking";

/**
 * 日程調整の予約UI（週表示＋入力モーダル）。
 * lead-dynamics の q/book.php を踏襲している。
 *
 * 公開ページ(/book/:slug)とウィジェット内パネルの両方から使うため、
 * 外枠を持たないコンポーネントにしてある。
 */

const WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"];
const pad = (n) => String(n).padStart(2, "0");
const dateKey = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** その日を含む週の月曜を返す */
const mondayOf = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = (d.getDay() + 6) % 7; // 月=0 になるように
  d.setDate(d.getDate() - diff);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

/** "HH:MM" に分を足して "HH:MM" で返す */
const addMinutes = (time, minutes) => {
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + minutes;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
};

// LD の aisdrLeadError と同じ拒否リスト。BtoBの商談なので会社ドメインだけ受ける。
const FREE_MAIL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.co.jp",
  "yahoo.com",
  "ymail.com",
  "rocketmail.com",
  "hotmail.com",
  "hotmail.co.jp",
  "outlook.com",
  "outlook.jp",
  "live.jp",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "proton.me",
  "protonmail.com",
  "yandex.com",
  "zoho.com",
  "docomo.ne.jp",
  "ezweb.ne.jp",
  "au.com",
  "softbank.ne.jp",
  "i.softbank.jp",
  "ybb.ne.jp",
];

/** 入力エラーを1つ返す。問題なければ空文字 */
export const leadError = ({ company, name, email, phone }) => {
  if (!company || !name || !email || !phone) {
    return "会社名・お名前・メール・電話番号をすべてご入力ください";
  }
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    return "メールアドレスの形式が正しくありません";
  }
  const domain = (email.split("@")[1] || "").toLowerCase();
  if (FREE_MAIL_DOMAINS.indexOf(domain) >= 0) {
    return "恐れ入りますが、会社のメールアドレスをご入力ください（フリーメール不可）";
  }
  return "";
};

const formatSlotLabel = (dateStr, time, duration) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const weekday = WEEK_LABELS[new Date(y, m - 1, d).getDay()];
  return `${m}月${d}日 (${weekday}) ${time} - ${addMinutes(time, duration)}`;
};

const BookingWeek = (props) => {
  const { slug, onDone } = props;

  const today = useMemo(() => new Date(), []);
  const [weekStart, setWeekStart] = useState(() => mondayOf(today));

  const [slotsByDate, setSlotsByDate] = useState({});
  const [profile, setProfile] = useState(null);
  const [duration, setDuration] = useState(60);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [selected, setSelected] = useState(null); // { date, time }
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(null);
  const gridRef = useRef(null);

  // LD と同じく平日5日ぶんを一度に見せる
  const days = useMemo(
    () => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const loadWeek = useCallback(
    async (start, { silent } = {}) => {
      if (!silent) setLoading(true);
      const from = dateKey(start);
      const to = dateKey(addDays(start, 4));
      const result = await fetchAvailability({ slug, from, to });
      if (!result.ok) {
        setLoadError(result.error);
        setLoading(false);
        return;
      }
      setLoadError("");
      setProfile(result.data.profile);
      setDuration(result.data.duration);
      setSlotsByDate((prev) => {
        const next = { ...prev };
        result.data.days.forEach((day) => {
          next[day.date] = day.slots;
        });
        return next;
      });
      setLoading(false);
    },
    [slug]
  );

  useEffect(() => {
    loadWeek(weekStart);
  }, [weekStart, loadWeek]);

  // スマホでは5列が入り切らず横スクロールになる（LD の book.php と同じ）。
  // 手前の曜日が全部「–」だと空きが無いように見えるので、空きのある日まで寄せる。
  useEffect(() => {
    const grid = gridRef.current;
    if (loading || !grid) return;
    if (grid.scrollWidth <= grid.clientWidth) return;
    // :has() は古い WebView に無いので、列を辿って探す
    const first = Array.prototype.find.call(
      grid.querySelectorAll(".bk-col"),
      (col) => col.querySelector(".bk-slot")
    );
    if (first) grid.scrollLeft = first.offsetLeft - grid.offsetLeft;
  }, [loading, slotsByDate, weekStart]);

  const moveWeek = (diff) => setWeekStart((prev) => addDays(prev, diff * 7));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const err = leadError(form);
    if (err) {
      setFormError(err);
      return;
    }
    setFormError("");
    setSubmitting(true);

    const result = await createBooking({
      slug,
      date: selected.date,
      time: selected.time,
      company: form.company.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      notes: form.notes.trim(),
      fromUrl: document.referrer,
    });
    setSubmitting(false);

    if (!result.ok) {
      setFormError(result.error);
      // 枠が埋まっていたら最新の空きに更新して選び直してもらう
      if (result.conflict) {
        setSelected(null);
        loadWeek(weekStart, { silent: true });
      }
      return;
    }
    const label = formatSlotLabel(selected.date, selected.time, duration);
    setDone(label);
    setSelected(null);
    if (onDone) onDone(label);
  };

  /* ---------------- 完了 ---------------- */
  if (done) {
    return (
      <div className="bk-done">
        <div className="bk-done-mark">✓</div>
        <h2>予約が完了しました</h2>
        <p className="bk-done-when">{done}</p>
        <p>
          ご登録のメールアドレス宛に確認メールをお送りしました。
          <br />
          当日は、メールに記載のURLからご参加ください。
        </p>
      </div>
    );
  }

  /* ---------------- リンク不正 ---------------- */
  if (loadError && !profile) {
    return (
      <div className="bk-done">
        <h2>ご利用いただけません</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  const weekLabel = `${
    weekStart.getMonth() + 1
  }/${weekStart.getDate()}(月) 〜 ${
    addDays(weekStart, 4).getMonth() + 1
  }/${addDays(weekStart, 4).getDate()}(金)`;

  return (
    <div className="bk-week-root">
      {/* 担当者 */}
      <header className="bk-head">
        {profile?.company && <p className="bk-company">{profile.company}</p>}
        <div className="bk-person">
          <div className="bk-avatar">
            {profile?.avatar ? (
              <img src={profile.avatar} alt="" />
            ) : (
              <span>{(profile?.username || "?").slice(0, 1)}</span>
            )}
          </div>
          <h2>{profile?.username || "オンライン商談"}</h2>
        </div>
        <p className="bk-lead">都合の良い日時を選択してください</p>
        <p className="bk-meta">オンライン商談（{duration}分）</p>
      </header>

      {/* 週ナビ */}
      <div className="bk-weeknav">
        <button
          type="button"
          onClick={() => moveWeek(-1)}
          aria-label="前の週"
          disabled={addDays(weekStart, -7) < mondayOf(today)}
        >
          ‹
        </button>
        <b>{weekLabel}</b>
        <button type="button" onClick={() => moveWeek(1)} aria-label="次の週">
          ›
        </button>
      </div>

      {/* 週表示 */}
      <div className="bk-grid" ref={gridRef}>
        {days.map((d) => {
          const key = dateKey(d);
          const slots = slotsByDate[key] ?? [];
          const isToday = key === dateKey(today);
          return (
            <div key={key} className={`bk-col ${isToday ? "today" : ""}`}>
              <div className="bk-col-head">
                <span className="bk-wd">{WEEK_LABELS[d.getDay()]}</span>
                <span className="bk-day">{d.getDate()}</span>
                <span className="bk-mon">{d.getMonth() + 1}月</span>
              </div>
              <div className="bk-col-body">
                {slots.length === 0 ? (
                  <span className="bk-empty">–</span>
                ) : (
                  slots.map((time) => (
                    <button
                      key={time}
                      type="button"
                      className="bk-slot"
                      onClick={() => {
                        setSelected({ date: key, time });
                        setFormError("");
                      }}
                    >
                      {time} - {addMinutes(time, duration)}
                    </button>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {loading && <p className="bk-note">空き状況を確認しています…</p>}
      {!loading && loadError && <p className="bk-note bk-err">{loadError}</p>}

      {/* 入力モーダル */}
      {selected && (
        <div
          className="bk-modal-back"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <form className="bk-modal" onSubmit={handleSubmit}>
            <button
              type="button"
              className="bk-modal-x"
              onClick={() => setSelected(null)}
              aria-label="閉じる"
            >
              ×
            </button>

            <p className="bk-chosen">
              <span className="bk-clock" aria-hidden="true">
                🕐
              </span>
              {formatSlotLabel(selected.date, selected.time, duration)}
            </p>

            <label>
              会社名 <i>*</i>
              <input
                type="text"
                value={form.company}
                placeholder="例: 株式会社サンプル"
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({ ...prev, company: v }));
                }}
              />
            </label>
            <label>
              お名前 <i>*</i>
              <input
                type="text"
                value={form.name}
                placeholder="例: 田中 太郎"
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({ ...prev, name: v }));
                }}
              />
            </label>
            <label>
              メールアドレス <i>*</i>
              <small>（会社ドメイン。フリー/キャリアメール不可）</small>
              <input
                type="email"
                value={form.email}
                placeholder="例: tanaka@your-company.co.jp"
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({ ...prev, email: v }));
                }}
              />
            </label>
            <label>
              電話番号 <i>*</i>
              <input
                type="tel"
                value={form.phone}
                placeholder="例: 03-1234-5678"
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({ ...prev, phone: v }));
                }}
              />
            </label>
            <label>
              備考欄
              <textarea
                rows={3}
                maxLength={1000}
                value={form.notes}
                placeholder="ご要望やご質問があればご記入ください"
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({ ...prev, notes: v }));
                }}
              />
            </label>

            {formError && <p className="bk-err">{formError}</p>}

            <button type="submit" className="bk-submit" disabled={submitting}>
              {submitting ? "送信中…" : "予約を確定する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default BookingWeek;
